// =============================================================================
// useUserAdminHandlers
// =============================================================================
// Async/CRUD handler logic extracted out of UserAdmin (AdminPages.jsx) to keep
// that component focused on state + render. UserAdmin still OWNS all the state
// (useState lives there); this hook just receives the state values + setters it
// needs as a single "bag" and returns the handler closures. Each render the
// component passes the latest values, so the closures never go stale.
//
// Handlers covered:
//   - Delivery-address modal: openDeliveryModal / startNewAddr / startEditAddr
//     / saveAddr / reactivateAddr / deleteAddr / setDefault (Phase A)
//   - User CRUD: saveEdit / sendPasswordReset / createUser / deactivateUser
//     (Phase B). These three writes that touch auth.users route through the
//     admin-users Edge Function (EDGE_URL, computed below).
//
// See also:
//   src/hooks/usePhoneList.js   — sibling extraction (phone-list CRUD)
//   src/components/admin/AdminPages.jsx — the UserAdmin consumer
// =============================================================================

import {
  ADDRESS_PROMOTION_THRESHOLD,
  clearDefaultForShelterId,
  insertAddresses,
  listAllByShelterId,
  updateAddressById,
} from "../data/deliveryAddresses";
import { getProfileByEmail, updateProfileById, upsertProfile } from "../data/profiles";
import { countRequestsByDeliveryAddressId } from "../data/requests";
import {
  getShelterByCode,
  insertShelter,
  listShelterIdsByPrimaryContact,
  listSheltersByCodes,
  updateShelterById,
} from "../data/shelters";
import {
  enqueueContactDeleteForMembership,
  enqueueContactPushForMembership,
  enqueueContactPushForUser,
  enqueueUnleashedPush,
} from "../data/unleashedPushQueue";
import {
  deactivateMembershipById,
  deleteUserShelter,
  deleteUserSheltersByUserId,
  insertContactMembership,
  insertUserShelters,
  isMembershipPrimaryContact,
  listAllUserShelters,
  listNoLoginContactsByEmail,
  listUserSheltersByUserId,
  reactivateMembershipById,
  setPrimaryContactMembership,
  setPrimaryShelter,
  setPrimaryShelterUser,
  shelterHasPrimaryUser,
  updateMembershipContactById,
  updateNoLoginContactsByIds,
  upsertUserShelter,
} from "../data/userShelters";
import { ADDRESS_NAME_REQUIRED_MSG, isBlankAddressName } from "../services/addressName";
import { computeContactChanges } from "../services/contactChangeSummary";
import { alertDialog, confirmDialog } from "../services/dialogService";
import type { DeliveryAddressRow } from "../types/database.js";
import { logAudit } from "../utils/auditLog";
import { ROLES } from "../utils/enums";
import { GLYPH } from "../utils/glyphs";
import { logger } from "../utils/logger";
import { formatPhone } from "../utils/sanitize";
import { getSupabaseClient } from "../utils/supabase";

interface EditForm {
  shelter_name?: string;
  shelter_code?: string;
  state?: string;
  customer_type?: string;
  sales_group?: string;
  taxable?: boolean;
  stock_held?: boolean;
  postal_line1?: string;
  postal_line2?: string;
  postal_suburb?: string;
  postal_suburb_city?: string;
  postal_state?: string;
  postal_postcode?: string;
  unleashed_guid?: string;
  internal_notes?: string;
  date_onboarded?: string;
  role?: string;
  first_name?: string;
  last_name?: string;
  is_generic?: boolean;
  shelter_codes?: string[];
  user_email?: string;
  display_name?: string;
  phones?: unknown[];
  primary_contact_label?: string;
  // Contact-model 3c-iii: the caseworker edit form now carries a fixed Phone +
  // Mobile (replacing the free-form phones list) and a per-service Contact Role.
  phone?: string;
  mobile?: string;
  contact_label?: string;
  // Contact-model No-Login: the "No login account (contact only)" tickbox. Ticking on
  // a login user removes their login; un-ticking on a login-less contact creates one.
  no_login?: boolean;
  login_method?: string;
  // Zephyr Staff editor-only fields (profiles.staff_mobile / staff_colour /
  // show_on_staff_list) — db-105.
  staff_mobile?: string;
  staff_colour?: string;
  show_on_staff_list?: boolean;
  // db-184: the shelter-facing "Display Name" for a Zephyr staff member (e.g.
  // "John W (Zephyr)"). Staff rows only; never written on a caseworker row.
  preferred_name?: string;
  // db-139: a ZEPHYR person on a non-Zephyr email domain (a consultant). Internal
  // roles only — never written on a caseworker row.
  is_consultant?: boolean;
}
interface NewUserForm {
  email?: string;
  password?: string;
  first_name?: string;
  last_name?: string;
  shelter_name?: string;
  shelter_code?: string;
  // Optional: when the caller already knows the shelter's id (e.g. the first-user
  // path just created the service), pass it so the membership write skips a lookup.
  shelter_id?: string;
  role?: string;
  state?: string;
  customer_type?: string;
  sales_group?: string;
  taxable?: boolean;
  stock_held?: boolean;
  phone?: string;
  mobile?: string;
  abn?: string;
  default_warehouse?: string;
  date_onboarded?: string;
  reminder?: string;
  primary_contact_label?: string;
  login_method?: string;
  send_welcome?: boolean;
  // Zephyr Staff create-only fields (parity with the edit form): show on the
  // Resources → Staff List (db-105) and the consultant flag (db-139). Only ever
  // written on a staff account (role !== shelter), never on a caseworker row.
  show_on_staff_list?: boolean;
  is_consultant?: boolean;
  // db-184: the shelter-facing "Display Name" for a Zephyr staff member. Staff
  // accounts only (role !== shelter).
  preferred_name?: string;
  // "+ New User" contact-only path: create a login-less membership (no auth account).
  no_login?: boolean;
  postal_line1?: string;
  postal_line2?: string;
  postal_suburb?: string;
  postal_suburb_city?: string;
  postal_state?: string;
  postal_postcode?: string;
  physical_line1?: string;
  physical_line2?: string;
  physical_suburb?: string;
  physical_suburb_city?: string;
  physical_state?: string;
  physical_postcode?: string;
  internal_notes?: string;
  delivery_line1?: string;
  delivery_line2?: string;
  delivery_suburb?: string;
  delivery_state?: string;
  delivery_postcode?: string;
  delivery_instructions?: string;
  delivery_address_name?: string;
  // Wizard "Users" step: the first caseworker to create after the service. Kept
  // under cw_* keys so they don't collide with the service's own email/phone.
  cw_first_name?: string;
  cw_last_name?: string;
  cw_email?: string;
  cw_password?: string;
  cw_phone?: string;
  cw_mobile?: string;
  cw_primary_contact_label?: string;
  // Wizard "Users" step: create the first person as a contact-only record (no
  // login account) — a login-less user_shelters membership, made the service's
  // Primary contact. They can be given a login later from their edit form.
  cw_no_login?: boolean;
}
interface AddrForm {
  address_name?: string;
  street?: string;
  street2?: string;
  suburb?: string;
  // Suburb/City alignment (db-87): the single displayed locality on delivery_addresses.
  suburb_city?: string;
  state?: string;
  postcode?: string;
  delivery_instruction?: string;
  is_default?: boolean;
  // db-146/147 status fields — staged in the edit modal, committed on Save.
  banned_address?: boolean;
  silent_address?: boolean;
  deactivated?: boolean;
  use_count?: number;
}
interface EditingState {
  id?: string;
  // "shelters" | "profiles" | "nologin" (a login-less contact — membership only).
  table?: string;
  originalEmail?: string;
  // The contact's name BEFORE this edit — used to fan a person-level change out only
  // to their genuine other-service memberships (same name+email), never to a different
  // person who merely shares a mailbox (the shared-mailbox fan-out fix, 30 Aug 2026).
  originalFirstName?: string;
  originalLastName?: string;
  // Contact-model 3c-iii (GOTCHA 2): the id of the service whose Users tab opened
  // this edit, so the per-service Contact Role writes to the RIGHT membership. Unset
  // for the Zephyr-Staff editor (staff have no memberships / no contact).
  shelterId?: string;
  // Contact-model No-Login: the user_shelters row id when editing a login-less contact.
  membershipId?: string;
}
interface AdminUser {
  email?: string;
  id?: string;
}
interface DeactivateTarget {
  id?: string;
  deactivated?: boolean;
  staff_user_email?: string;
}
interface DeliveryModalRef {
  id?: string;
  code?: string;
  name?: string;
}
interface UserAdminBag {
  user: AdminUser;
  addrForm: AddrForm;
  showDeliveryModal: DeliveryModalRef;
  editingAddr: number | "new" | null;
  deliveryAddrs: DeliveryAddressRow[];
  setShowDeliveryModal: (v: DeliveryModalRef | null) => void;
  setDeliveryLoading: (v: boolean) => void;
  setEditingAddr: (v: number | "new" | null) => void;
  setDeliveryAddrs: (v: DeliveryAddressRow[]) => void;
  setAddrForm: (v: AddrForm) => void;
  setDeliverySaving: (v: boolean) => void;
  showSaved: (msg?: string) => void;
  form: EditForm;
  editing: EditingState | null;
  newForm: NewUserForm;
  activeSection: string;
  setSaving: (v: boolean) => void;
  setEditing: (v: EditingState | null) => void;
  setNewErr: (v: string) => void;
  setNewSaving: (v: boolean) => void;
  setShowNew: (v: boolean) => void;
  setNewForm: (v: NewUserForm) => void;
  setConfirmDeactivate: (v: unknown) => void;
  loadUsers: () => void;
}

// A fully-blank New User/Service form (used to reset after a create).
const BLANK_NEW_USER_FORM: NewUserForm = {
  email: "",
  password: "",
  first_name: "",
  last_name: "",
  shelter_name: "",
  shelter_code: "",
  role: "shelter",
  state: "",
  customer_type: "",
  sales_group: "",
  taxable: false,
  phone: "",
  mobile: "",
  primary_contact_label: "Shelter Manager",
  postal_line1: "",
  postal_suburb: "",
  postal_postcode: "",
  internal_notes: "",
  delivery_line1: "",
  delivery_line2: "",
  delivery_suburb: "",
  delivery_state: "",
  delivery_postcode: "",
  delivery_instructions: "",
  delivery_address_name: "",
};

// db-190 — a login-less contact create/edit that collides with the active-scoped
// uniqueness index (same DFV Service + first + last + email, where a blank email now
// counts as '') comes back as a raw Postgres unique-violation. Translate it to a human
// message; pass anything else straight through.
function contactDupMessage(rawMsg: string | null | undefined): string {
  const m = rawMsg || "";
  if (/duplicate key|unique constraint|already exists/i.test(m)) {
    return "There is already an active contact with the same name and email on this DFV Service. Edit or deactivate that one first.";
  }
  return m;
}

export function useUserAdminHandlers(bag: UserAdminBag) {
  function sb() {
    return getSupabaseClient()!;
  }
  const user = bag.user;
  const addrForm = bag.addrForm;
  const showDeliveryModal = bag.showDeliveryModal;
  const editingAddr = bag.editingAddr;
  const deliveryAddrs = bag.deliveryAddrs;
  const setShowDeliveryModal = bag.setShowDeliveryModal;
  const setDeliveryLoading = bag.setDeliveryLoading;
  const setEditingAddr = bag.setEditingAddr;
  const setDeliveryAddrs = bag.setDeliveryAddrs;
  const setAddrForm = bag.setAddrForm;
  const setDeliverySaving = bag.setDeliverySaving;
  const showSaved = bag.showSaved;
  // User-CRUD bag (Phase B)
  const form = bag.form;
  const editing = bag.editing;
  const newForm = bag.newForm;
  const activeSection = bag.activeSection;
  const setSaving = bag.setSaving;
  const setEditing = bag.setEditing;
  const setNewErr = bag.setNewErr;
  const setNewSaving = bag.setNewSaving;
  const setShowNew = bag.setShowNew;
  const setNewForm = bag.setNewForm;
  const setConfirmDeactivate = bag.setConfirmDeactivate;
  const loadUsers = bag.loadUsers;

  const EDGE_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-users`;

  function openDeliveryModal(
    shelterId: string | undefined,
    shelterCode: string | undefined,
    shelterName: string | undefined
  ) {
    setShowDeliveryModal({ id: shelterId, code: shelterCode, name: shelterName });
    setDeliveryLoading(true);
    setEditingAddr(null);
    listAllByShelterId(shelterId).then(function (rows) {
      // repo returns the generated row type; the admin form/state uses the
      // hand-written DeliveryAddressRow — bridge until those are unified.
      setDeliveryAddrs((rows || []) as unknown as DeliveryAddressRow[]);
      setDeliveryLoading(false);
    });
  }

  function startNewAddr() {
    setEditingAddr("new");
    setAddrForm({
      address_name: "",
      street: "",
      street2: "",
      suburb: "",
      suburb_city: "",
      state: "",
      postcode: "",
      delivery_instruction: "",
      is_default: false,
    });
  }

  function startEditAddr(addr: AddrForm, idx: number) {
    setEditingAddr(idx);
    setAddrForm({
      address_name: addr.address_name || "",
      street: addr.street || "",
      street2: addr.street2 || "",
      suburb: addr.suburb || "",
      suburb_city: addr.suburb_city || addr.suburb || "",
      state: addr.state || "",
      postcode: addr.postcode || "",
      delivery_instruction: addr.delivery_instruction || "",
      is_default: !!addr.is_default,
      // db-146/147 status fields — staged in the modal, committed on Save (all
      // address status changes now happen there; the list is display-only).
      banned_address: !!addr.banned_address,
      silent_address: !!addr.silent_address,
      deactivated: !!addr.deactivated,
      use_count: typeof addr.use_count === "number" ? addr.use_count : 0,
    });
  }

  function saveAddr() {
    if (!addrForm.street) {
      logger.warn("Street Address is required");
      return;
    }
    // Address Name is REQUIRED (David, 25 Oct 2026) — it prints on the delivery, so it
    // is no longer auto-filled as "Delivery N". The modal disables Save while it is
    // blank; this is the defensive backstop.
    if (isBlankAddressName(addrForm.address_name)) {
      void alertDialog({ title: "Address Name needed", message: ADDRESS_NAME_REQUIRED_MSG });
      return;
    }
    // Guardrail backstop (John, 8 Aug): the DEFAULT address can't be made unusable
    // (banned/silent/deactivated) — another must be promoted to default first. The
    // modal disables Save for this, so this is a defensive backstop.
    if (editingAddr !== "new") {
      const stored = deliveryAddrs[editingAddr as number];
      const makingUnusable = !!(
        addrForm.banned_address ||
        addrForm.silent_address ||
        addrForm.deactivated
      );
      if (stored?.is_default && makingUnusable) {
        void alertDialog({
          title: "Set another default first",
          message:
            "This is the default delivery address. Set another address as the default before " +
            "you ban, silence or deactivate this one.",
        });
        return;
      }
    }
    setDeliverySaving(true);
    const name = String(addrForm.address_name).trim();
    const row = Object.assign({}, addrForm, {
      shelter_id: showDeliveryModal.id,
      address_name: name,
    });
    function afterSave(res: { error: { message: string } | null }) {
      setDeliverySaving(false);
      if (res.error) {
        logger.warn("Save failed:", res.error.message);
        return;
      }
      setEditingAddr(null);
      openDeliveryModal(showDeliveryModal.id, showDeliveryModal.code, showDeliveryModal.name);
      showSaved(`Address saved ${GLYPH.check}`);
      // Auto-sync (db-133): a delivery-address change folds into the Customer push.
      void enqueueUnleashedPush({
        entityType: "customer",
        entityId: showDeliveryModal.id,
        shelterCode: showDeliveryModal.code,
      });
    }
    function doWrite() {
      if (editingAddr === "new") {
        // A supervisor-added address is known-good → seed use_count at the promotion
        // threshold so it shows on the shelter pick-list immediately (db-146).
        insertAddresses([Object.assign({}, row, { use_count: ADDRESS_PROMOTION_THRESHOLD })]).then(
          afterSave
        );
      } else {
        // EDIT: the modal stages the status fields (banned/silent/deactivated/use_count).
        // Enforce the db-146/147 invariants at Save: silent ⇒ trusted + promoted; and
        // use_count ≥ threshold ⟺ zephyr_verified (so the pick-list + approval hold stay
        // consistent). Count is clamped to 0…threshold.
        const silent = !!row.silent_address;
        const count = silent
          ? ADDRESS_PROMOTION_THRESHOLD
          : Math.max(0, Math.min(ADDRESS_PROMOTION_THRESHOLD, Number(row.use_count) || 0));
        const editRow = Object.assign({}, row, {
          use_count: count,
          zephyr_verified: silent || count >= ADDRESS_PROMOTION_THRESHOLD,
        });
        updateAddressById(deliveryAddrs[editingAddr as number].id, editRow).then(afterSave);
      }
    }
    // Only one address per shelter is the default — clear the others first when this
    // save sets it (the "Set Default" button used to do this; it now lives in the modal).
    if (row.is_default) {
      clearDefaultForShelterId(showDeliveryModal.id).then(doWrite);
    } else {
      doWrite();
    }
  }

  async function reactivateAddr(id: string) {
    // Restore a soft-deleted delivery_address by clearing the deactivated flag.
    const { error } = await updateAddressById(id, { deactivated: false });
    if (error) {
      logger.warn("Reactivate address failed:", error.message);
      return;
    }
    logAudit("delivery_address_reactivated", {
      id: id,
      shelter_code: showDeliveryModal.code,
      by: user?.email || "",
    });
    openDeliveryModal(showDeliveryModal.id, showDeliveryModal.code, showDeliveryModal.name);
    showSaved(`Address reactivated ${GLYPH.check}`);
    // Auto-sync (db-133): a delivery-address change folds into the Customer push.
    void enqueueUnleashedPush({
      entityType: "customer",
      entityId: showDeliveryModal.id,
      shelterCode: showDeliveryModal.code,
    });
  }

  // Promotion model (db-146): the ADMIN screen's action is the supervisor
  // fast-track — jump the address straight to the promotion threshold (shown on the
  // shelter pick-list + trusted). The per-step +1 verify lives on the REQUEST side
  // (RequestDetail.verifyDeliveryAddress), not here.
  async function promoteAddr(id: string) {
    const { error } = await updateAddressById(id, {
      use_count: ADDRESS_PROMOTION_THRESHOLD,
      zephyr_verified: true,
    });
    if (error) {
      logger.warn("Promote address failed:", error.message);
      return;
    }
    logAudit("delivery_address_promoted", {
      id: id,
      shelter_code: showDeliveryModal.code,
      by: user?.email || "",
    });
    openDeliveryModal(showDeliveryModal.id, showDeliveryModal.code, showDeliveryModal.name);
    showSaved(`Address added to the list ${GLYPH.check}`);
  }

  // Ban / unban (db-146): a banned address is never shown to the shelter and
  // HARD-BLOCKS approval on any request that uses it (resolution = re-route or
  // unban). Changing it alters the Unleashed shipping set, so re-queue the push.
  async function setAddrBanned(id: string, banned: boolean) {
    const { error } = await updateAddressById(id, { banned_address: banned });
    if (error) {
      logger.warn("Ban address failed:", error.message);
      return;
    }
    logAudit("delivery_address_banned", {
      id: id,
      banned: banned,
      shelter_code: showDeliveryModal.code,
      by: user?.email || "",
    });
    void enqueueUnleashedPush({
      entityType: "customer",
      entityId: showDeliveryModal.id,
      shelterCode: showDeliveryModal.code,
    });
    openDeliveryModal(showDeliveryModal.id, showDeliveryModal.code, showDeliveryModal.name);
    showSaved(banned ? `Address banned ${GLYPH.check}` : `Ban removed ${GLYPH.check}`);
  }

  // Silent / un-silent (db-146): a silent address is usable but permanently off the
  // shelter pick-list. Marking it silent VETS it once — auto-sets use_count to the
  // threshold + zephyr_verified true (trusted, off the pending badge, off the list).
  // Un-silencing just clears the flag (count/verified stay). Changes the Unleashed
  // shipping set, so re-queue the push.
  async function setAddrSilent(id: string, silent: boolean) {
    const patch = silent
      ? { silent_address: true, use_count: ADDRESS_PROMOTION_THRESHOLD, zephyr_verified: true }
      : { silent_address: false };
    const { error } = await updateAddressById(id, patch);
    if (error) {
      logger.warn("Silent address failed:", error.message);
      return;
    }
    logAudit("delivery_address_silenced", {
      id: id,
      silent: silent,
      shelter_code: showDeliveryModal.code,
      by: user?.email || "",
    });
    void enqueueUnleashedPush({
      entityType: "customer",
      entityId: showDeliveryModal.id,
      shelterCode: showDeliveryModal.code,
    });
    openDeliveryModal(showDeliveryModal.id, showDeliveryModal.code, showDeliveryModal.name);
    showSaved(silent ? `Address set to silent ${GLYPH.check}` : `Silent removed ${GLYPH.check}`);
  }

  async function deleteAddr(id: string) {
    // Task #DB-2: soft-delete via deactivated=true. Admin can reactivate later
    // (see reactivateAddr above; openDeliveryModal shows all rows incl. deactivated).
    // Warn if any requests still reference this address — soft-delete won't
    // break them (the FK join still resolves), but the admin should know.
    const inUse = await countRequestsByDeliveryAddressId(id);
    if (inUse > 0) {
      const ok = await confirmDialog({
        title: "Deactivate address?",
        message:
          `This address is used by ${inUse} existing request${inUse === 1 ? "" : "s"}.\n\n` +
          `Existing requests will still display it correctly. New requests won't see it ` +
          `in the dropdown.\n\nDeactivate anyway?`,
        confirmLabel: "Deactivate",
        danger: true,
      });
      if (!ok) return;
    }
    const { error } = await updateAddressById(id, { deactivated: true });
    if (error) {
      logger.warn("Deactivate address failed:", error.message);
      return;
    }
    logAudit("delivery_address_deactivated", {
      id: id,
      shelter_code: showDeliveryModal.code,
      in_use_count: inUse,
      by: user?.email || "",
    });
    openDeliveryModal(showDeliveryModal.id, showDeliveryModal.code, showDeliveryModal.name);
    showSaved(`Address deactivated ${GLYPH.check}`);
    // Auto-sync (db-133): a delivery-address change folds into the Customer push.
    //
    // The Customer POST REPLACES the whole Addresses array (customerMap.buildCustomerAddresses),
    // and pushCustomer.loadShelterDelivery only loads addresses that are active, not banned and
    // not silent — so deactivating one DOES remove it from Unleashed. The ONE exception: when
    // the portal's active set is empty, buildCustomerAddresses carries Unleashed's existing
    // Shipping untouched (a guard against blanking not-yet-imported addresses), so deactivating
    // the LAST active address leaves it there. (This comment previously claimed a removed
    // address can't be deleted via the Customer POST — it was written the same day replace
    // semantics landed and was never reconciled. Finding B2, 18 Aug 2026.)
    void enqueueUnleashedPush({
      entityType: "customer",
      entityId: showDeliveryModal.id,
      shelterCode: showDeliveryModal.code,
    });
  }

  function setDefault(id: string) {
    // Clear all defaults for this shelter, then set the chosen one
    clearDefaultForShelterId(showDeliveryModal.id).then(function () {
      updateAddressById(id, { is_default: true }).then(function () {
        openDeliveryModal(showDeliveryModal.id, showDeliveryModal.code, showDeliveryModal.name);
        // Auto-sync (db-133): IsDefault change folds into the Customer push.
        void enqueueUnleashedPush({
          entityType: "customer",
          entityId: showDeliveryModal.id,
          shelterCode: showDeliveryModal.code,
        });
      });
    });
  }

  // Contact-model No-Login: save a login-less contact (a user_shelters row with no
  // profile). Two paths off the "No login account" tickbox:
  //  - stays ticked → update the membership's contact fields only;
  //  - un-ticked → CREATE a login account for the person (silent — random password,
  //    no welcome; invited later via Manage → Invitations) and LINK this existing
  //    membership to it (set user_id) rather than creating a second membership. If the
  //    email already belongs to a login, the EF create fails and we surface it.
  async function saveNoLoginContact() {
    const membershipId = editing!.membershipId ?? "";
    const email = (form.user_email || "").trim();
    const contactPatch = {
      first_name: form.first_name || null,
      last_name: form.last_name || null,
      email: email || null,
      phone: formatPhone(form.phone, "landline") || null,
      mobile: formatPhone(form.mobile, "mobile") || null,
      contact_label: form.contact_label || null,
    };
    if (form.no_login === false) {
      if (!email) {
        setSaving(false);
        await alertDialog({ message: "A login account needs an email address." });
        return;
      }
      // If a login already exists for this email, ATTACH this membership to it instead of
      // creating a duplicate (auth rejects duplicate emails — the "already registered"
      // error). Confirm first: the email may belong to a DIFFERENT person (the shared-email
      // case), so never merge silently.
      const existing = await getProfileByEmail(email);
      if (existing) {
        setSaving(false);
        const who = existing.display_name || email;
        const ok = await confirmDialog({
          title: "Account already exists",
          message:
            `A login account already exists for ${email} (${who}). Attach this contact to that ` +
            `login so they can sign in and reach this DFV Service? Cancel if this is a DIFFERENT ` +
            `person — use a different email, or keep them as No Login.`,
          confirmLabel: "Attach to existing login",
          cancelLabel: "Cancel",
        });
        if (!ok) return;
        const linkExisting = await updateMembershipContactById(
          membershipId,
          Object.assign({}, contactPatch, { user_id: existing.id })
        );
        if (linkExisting.error) {
          await alertDialog({ message: `Could not attach:\n\n${linkExisting.error.message}` });
          return;
        }
        logAudit("no_login_contact_linked", {
          membership_id: membershipId,
          user_id: existing.id,
          email: email,
          by: user.email,
        });
        setEditing(null);
        loadUsers();
        // Contacts arm: the membership's contact now belongs to an existing login → upsert.
        void enqueueContactPushForMembership(membershipId, "upsert");
        showSaved(`Attached to existing login ${GLYPH.check}`);
        return;
      }
      const randomPassword = `Zx${crypto.randomUUID().replace(/-/g, "")}A9!`;
      const sessionRes = await sb().auth.getSession();
      const token = sessionRes.data?.session ? sessionRes.data.session.access_token : "";
      let res: { error?: string; user_id?: string; user?: { id?: string }; reclaimed?: boolean };
      try {
        const resp = await fetch(EDGE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            action: "create",
            email: email,
            password: randomPassword,
            display_name: `${form.first_name || ""} ${form.last_name || ""}`.trim(),
            role: ROLES.SHELTER,
            first_name: form.first_name || "",
            last_name: form.last_name || "",
            is_generic: false,
            login_method: "password",
            send_welcome: false,
          }),
        });
        res = await resp.json();
      } catch (e) {
        setSaving(false);
        await alertDialog({
          message: `Failed to create login — network error.\n\n${(e as Error)?.message ? (e as Error).message : String(e)}`,
        });
        return;
      }
      if (res.error) {
        setSaving(false);
        await alertDialog({ message: `Failed to create login:\n\n${res.error}` });
        return;
      }
      const newUserId = res.user_id || res.user?.id || null;
      if (!newUserId) {
        setSaving(false);
        await alertDialog({ message: "Login created but no user id was returned." });
        return;
      }
      // Link the EXISTING membership to the new account (+ refresh contact fields).
      const linkRes = await updateMembershipContactById(
        membershipId,
        Object.assign({}, contactPatch, { user_id: newUserId })
      );
      setSaving(false);
      if (linkRes.error) {
        await alertDialog({
          message: `Login created but linking the contact failed:\n\n${linkRes.error.message}`,
        });
        return;
      }
      logAudit("no_login_contact_promoted", {
        membership_id: membershipId,
        new_user_id: newUserId,
        email: email,
        by: user.email,
      });
      // Offer to send the invitation right here (rather than routing to Manage →
      // Invitations). Yes → the admin-users EF `send_invite` action + stamp invited_at.
      const invite = await confirmDialog({
        title: "Send invitation?",
        message:
          `Login created for ${email}. Send them an invitation email now so they can set a ` +
          `password and sign in? (You can also do this later from Manage → Invitations.)`,
        confirmLabel: "Yes, send invite",
        cancelLabel: "Not now",
      });
      if (invite) {
        try {
          const sr = await sb().auth.getSession();
          const tok = sr.data?.session ? sr.data.session.access_token : "";
          const iResp = await fetch(EDGE_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
            body: JSON.stringify({
              action: "send_invite",
              email: email,
              name: `${form.first_name || ""} ${form.last_name || ""}`.trim() || email,
            }),
          });
          const iRes = await iResp.json();
          if (iRes.error) {
            await alertDialog({
              message: `Login created, but the invite could not be sent:\n\n${iRes.error}`,
            });
          } else {
            await updateProfileById(newUserId, { invited_at: new Date().toISOString() });
            logAudit("invites_sent", {
              target_id: newUserId,
              email: email,
              by: user.email,
              via: "promote",
            });
          }
        } catch (_e) {
          await alertDialog({
            message: "Login created, but the invite could not be sent (network error).",
          });
        }
      }
      setEditing(null);
      loadUsers();
      // Contacts arm: the contact now has a login account → upsert.
      void enqueueContactPushForMembership(membershipId, "upsert");
      // "restored" when the EF reclaimed a previously-removed login for this email
      // (shared-email case), "created" when it was a brand-new account.
      const loginVerb = res.reclaimed ? "Login restored" : "Login created";
      showSaved(invite ? `${loginVerb} + invited ${GLYPH.check}` : `${loginVerb} ${GLYPH.check}`);
      return;
    }
    // Pure membership contact update (stays login-less).
    const res = await updateMembershipContactById(membershipId, contactPatch);
    if (res.error) {
      setSaving(false);
      logger.warn("Save failed:", res.error.message);
      // db-190: surface a collision (a rename/email edit onto an existing active contact)
      // as a human message instead of failing silently.
      await alertDialog({ message: contactDupMessage(res.error.message) });
      return;
    }
    setSaving(false);
    // Fan-out choice (John, 22 Jul; narrowed 30 Aug 2026): a demoted multi-service login
    // leaves the SAME person as separate login-less rows on their other services. If any
    // exist, offer to apply the person-level change there too — keeping each service's own
    // Contact Role. ⚠️ Match on name AND email as they were BEFORE this edit: a shared /
    // generic mailbox (e.g. welcomeq@…) groups DIFFERENT people on one email, so an
    // email-ONLY match would silently overwrite a stranger (the ZephyrQLD122 Donna
    // incident). Same-name rows = the same person → offered; different-name rows on the
    // same email = a shared mailbox → left untouched. Update by id, never a blanket email
    // UPDATE. See docs/contact-fan-out-shared-mailbox-fix-plan.md.
    const priorEmail = (editing!.originalEmail || "").trim();
    const priorFirst = (editing!.originalFirstName || "").trim().toLowerCase();
    const priorLast = (editing!.originalLastName || "").trim().toLowerCase();
    const matches = priorEmail
      ? (await listNoLoginContactsByEmail(priorEmail)).filter(function (r) {
          return r.id !== membershipId;
        })
      : [];
    const samePerson = matches.filter(function (r) {
      return (
        (r.first_name || "").trim().toLowerCase() === priorFirst &&
        (r.last_name || "").trim().toLowerCase() === priorLast
      );
    });
    const otherPeople = matches.filter(function (r) {
      return !samePerson.includes(r);
    });
    if (samePerson.length > 0) {
      const codes = samePerson
        .map(function (r) {
          return r.shelter_code;
        })
        .filter(Boolean)
        .join(", ");
      const ok = await confirmDialog({
        title: "Update on their other DFV Services?",
        message:
          `This person is also on ${samePerson.length} other DFV Service${samePerson.length === 1 ? "" : "s"}` +
          `${codes ? ` (${codes})` : ""}. Update their name, email, phone and mobile there too? ` +
          `(Each service keeps its own Contact Role.)`,
        confirmLabel: "Yes, update all",
        cancelLabel: "No, just this one",
      });
      if (ok) {
        await updateNoLoginContactsByIds(
          samePerson.map(function (r) {
            return r.id;
          }),
          {
            first_name: contactPatch.first_name,
            last_name: contactPatch.last_name,
            email: contactPatch.email,
            phone: contactPatch.phone,
            mobile: contactPatch.mobile,
          }
        );
        logAudit("no_login_contact_fanned_out", {
          email: contactPatch.email,
          count: samePerson.length,
          by: user.email,
        });
        // Contacts arm: the person's other login-less contacts changed too → upsert each.
        for (const r of samePerson) {
          void enqueueContactPushForMembership(r.id, "upsert");
        }
      }
    }
    // Different-named contacts on the same email = a shared mailbox, NOT this person.
    // Never touched; tell the admin so the untouched rows aren't a surprise.
    if (otherPeople.length > 0) {
      await alertDialog({
        title: "Shared email — other contacts left unchanged",
        message:
          `${otherPeople.length} other contact${otherPeople.length === 1 ? "" : "s"} share this email but ` +
          `have a different name, so ${otherPeople.length === 1 ? "it was" : "they were"} not changed. ` +
          `(If any is really the same person, edit that contact directly.)`,
      });
    }
    logAudit("no_login_contact_updated", { membership_id: membershipId, by: user.email });
    // Contacts arm: the login-less contact's details changed → upsert.
    void enqueueContactPushForMembership(membershipId, "upsert");
    setEditing(null);
    loadUsers();
    showSaved(`Contact updated ${GLYPH.check}`);
  }

  // db-189 — DEACTIVATE a login-less contact (the no-delete convention). Keeps the
  // membership row, flags it deactivated, nulls the GUID, and deletes its Unleashed
  // contact. BLOCK-until-repointed: a contact that is still a service's Primary can't be
  // deactivated — the operator must choose a new Primary first (the db-189 trigger also
  // enforces this, but we check up-front so we never enqueue an Unleashed delete for a
  // contact we then fail to deactivate). Reactivate re-creates the Unleashed contact.
  async function removeNoLoginContact(row: { membershipId?: string; display_name?: string }) {
    const who = row.display_name || "this contact";
    const membershipId = row.membershipId ?? "";
    if (
      !(await confirmDialog({
        title: "Deactivate contact?",
        message: `Deactivate ${who} on this DFV Service? The contact record is kept but hidden, and its Unleashed contact is removed. You can reactivate it later.`,
        confirmLabel: "Deactivate",
        danger: true,
      }))
    )
      return;
    // Block-until-repointed: refuse if this contact is still the service's Primary.
    if (await isMembershipPrimaryContact(membershipId)) {
      await alertDialog({
        title: "Choose a new Primary first",
        message: `${who} is the Primary contact for this DFV Service. Set a different Primary contact before deactivating this one.`,
      });
      return;
    }
    // Unleashed Contacts arm: capture the GUIDs + enqueue the contact DELETE BEFORE the
    // deactivate NULLs the GUID (the worker can't read it back afterwards). Awaited so the
    // capture wins the race; Zephyr-gated + no-op when never linked.
    await enqueueContactDeleteForMembership(membershipId);
    const res = await deactivateMembershipById(membershipId);
    if (res.error) {
      await alertDialog({ message: `Could not deactivate the contact:\n\n${res.error.message}` });
      return;
    }
    logAudit("no_login_contact_deactivated", { membership_id: membershipId, by: user.email });
    loadUsers();
    showSaved(`Contact deactivated ${GLYPH.check}`);
  }

  // db-189 — REACTIVATE a soft-deleted login-less contact: restore the row + re-create its
  // Unleashed contact (new GUID) by enqueuing an upsert. If an ACTIVE contact with the same
  // service+name+email already exists, the active-scoped unique index (db-189) rejects it —
  // surfaced as a friendly message, not a raw DB error.
  async function reactivateNoLoginContact(row: { membershipId?: string; display_name?: string }) {
    const who = row.display_name || "this contact";
    const membershipId = row.membershipId ?? "";
    if (
      !(await confirmDialog({
        title: "Reactivate contact?",
        message: `Reactivate ${who}? This restores the contact and re-creates it in Unleashed (a new link).`,
        confirmLabel: "Reactivate",
      }))
    )
      return;
    const res = await reactivateMembershipById(membershipId);
    if (res.error) {
      const dup = /duplicate|unique|already exists/i.test(res.error.message);
      await alertDialog({
        message: dup
          ? "There is already an active contact with the same name and email on this DFV Service. Edit or remove that one before reactivating this record."
          : `Could not reactivate the contact:\n\n${res.error.message}`,
      });
      return;
    }
    void enqueueContactPushForMembership(membershipId, "upsert");
    logAudit("no_login_contact_reactivated", { membership_id: membershipId, by: user.email });
    loadUsers();
    showSaved(`Contact reactivated ${GLYPH.check}`);
  }

  // Contact-model No-Login: retire a login user's account, keeping them as contact-only
  // records. Guard: no service they belong to may be left with zero login users. Then
  // (confirmed) the admin-users EF nulls their memberships' user_id, deactivates the
  // profile, and bans the login (ban+deactivate — the profile row is KEPT, FK-safe).
  async function removeLoginForUser() {
    const userId = editing!.id ?? "";
    const all = (await listAllUserShelters()) || [];
    const theirShelterIds = all
      .filter(function (m) {
        return m.user_id === userId;
      })
      .map(function (m) {
        return m.shelter_id;
      })
      .filter(function (id): id is string {
        return !!id;
      });
    const orphaned: string[] = [];
    theirShelterIds.forEach(function (sid) {
      const otherLogins = all.filter(function (m) {
        return m.shelter_id === sid && m.user_id && m.user_id !== userId;
      });
      if (otherLogins.length === 0) {
        const code =
          all.find(function (m) {
            return m.shelter_id === sid;
          })?.shelter_code || sid;
        if (orphaned.indexOf(code) === -1) orphaned.push(code);
      }
    });
    if (orphaned.length > 0) {
      setSaving(false);
      await alertDialog({
        title: "Can't remove login",
        message:
          `Removing this login would leave ${orphaned.length === 1 ? "a DFV Service" : "these DFV Services"} ` +
          `with no login users: ${orphaned.join(", ")}.\n\nAdd or promote another login user there first.`,
      });
      return;
    }
    if (
      !(await confirmDialog({
        title: "Remove login?",
        message:
          "This removes the person's login account (they can no longer sign in) and keeps them as a " +
          "contact-only record on every DFV Service they belong to. Continue?",
        confirmLabel: "Remove login",
        danger: true,
      }))
    ) {
      setSaving(false);
      return;
    }
    const sessionRes = await sb().auth.getSession();
    const token = sessionRes.data?.session ? sessionRes.data.session.access_token : "";
    let res: { error?: string; success?: boolean } | undefined;
    try {
      const resp = await fetch(EDGE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: "remove_login", user_id: userId }),
      });
      res = await resp.json();
    } catch (e) {
      setSaving(false);
      await alertDialog({
        message: `Failed to remove login — network error.\n\n${(e as Error)?.message ? (e as Error).message : String(e)}`,
      });
      return;
    }
    if (res?.error) {
      setSaving(false);
      await alertDialog({ message: `Failed to remove login:\n\n${res.error}` });
      return;
    }
    setSaving(false);
    logAudit("login_removed", { target_id: userId, by: user.email });
    setEditing(null);
    loadUsers();
    showSaved(`Login removed — now a contact ${GLYPH.check}`);
  }

  // Change the login email via the admin-users EF (it updates BOTH auth.users.email
  // and the profile mirror with the service-role client, so they stay in sync).
  // Shared by the staff and caseworker save paths — the ONE thing the two
  // populations legitimately have in common is that both are logins.
  // Returns false when the caller should abort (cancelled, empty, or failed).
  async function changeLoginEmailIfNeeded(): Promise<boolean> {
    const newEmail = (form.user_email || "").trim();
    const oldEmail = (editing?.originalEmail || "").trim();
    if (newEmail === oldEmail) return true;
    if (!newEmail) {
      setSaving(false);
      await alertDialog({ message: "Login email cannot be empty." });
      return false;
    }
    const isSelfEdit = editing!.id === user.id;
    const confirmMsg =
      `Change login email from\n  ${oldEmail || "(none)"}\nto\n  ${newEmail}?` +
      "\n\nThe user will need to use the new email to log in." +
      (isSelfEdit
        ? "\n\nWARNING: You are changing YOUR OWN login email. You will need to log out and back in using the new email after this saves."
        : "");
    if (!(await confirmDialog({ message: confirmMsg }))) {
      setSaving(false);
      return false;
    }
    const sessionRes = await sb().auth.getSession();
    const token = sessionRes.data?.session ? sessionRes.data.session.access_token : "";
    let emailRes: { error?: string } | undefined;
    try {
      const resp = await fetch(EDGE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          action: "update_email",
          user_id: editing!.id,
          new_email: newEmail,
        }),
      });
      emailRes = await resp.json();
    } catch (e) {
      setSaving(false);
      await alertDialog({
        message: `Failed to update login email — network error. Please try again.\n\n${
          (e as Error)?.message ? (e as Error).message : String(e)
        }`,
      });
      return false;
    }
    if (emailRes?.error) {
      setSaving(false);
      await alertDialog({ message: `Failed to update login email:\n\n${emailRes.error}` });
      return false;
    }
    logAudit("user_email_changed", {
      target_id: editing!.id,
      old_email: oldEmail,
      new_email: newEmail,
      by: user.email,
    });
    return true;
  }

  // ── Save a ZEPHYR STAFF account ─────────────────────────────────────────────
  // SEPARATE from the caseworker save on purpose (John, 6 Aug 2026): Zephyr staff
  // and DFV Service contacts are different populations and must not share a save
  // path — a shared one is how staff-only fields ended up on caseworker rows.
  // See docs/staff-caseworker-separation-plan.md.
  //
  // A staff account has NO DFV Service membership and NO Unleashed contact, so this
  // function deliberately does none of the caseworker machinery: no membership
  // delete/reinsert, no contact fan-out, no primary-contact pointer, no contact GUID
  // carry, no Unleashed enqueue.
  //
  // ⚠️ Two behaviours the shared path used to apply to staff, now correctly gone:
  //    • it ran deleteUserSheltersByUserId on every save (deleting any membership
  //      rows a staff account had — incidental, never intended as a feature);
  //    • it enqueued an Unleashed CONTACT push for a Zephyr staff member.
  async function saveStaffEdit() {
    if (!editing) return;
    setSaving(true);
    if (!(await changeLoginEmailIfNeeded())) return;
    const hasName = (form.first_name || "").trim() || (form.last_name || "").trim();
    const displayName = hasName
      ? `${form.first_name} ${form.last_name}`.trim()
      : form.display_name || "";
    const res = await updateProfileById(editing.id, {
      staff_first_name: form.first_name,
      staff_last_name: form.last_name,
      display_name: displayName,
      // "Display Name" on the staff screen = the SHORT public name shown to shelters (db-184).
      // Blank → NULL, and the session (attributedName) then computes "<first> <last-initial>
      // (Zephyr)" so nothing full-name ever leaks.
      preferred_name: (form.preferred_name || "").trim() || null,
      role: form.role,
      login_method: form.login_method || "password",
      staff_mobile: form.staff_mobile || null,
      staff_colour: form.staff_colour || null,
      show_on_staff_list: form.show_on_staff_list !== false,
      // db-139 — a Zephyr person on a non-Zephyr email domain. Only ever written
      // here, in the staff-only path.
      is_consultant: form.is_consultant === true,
      updated_by: user.email,
    });
    setSaving(false);
    if (res.error) {
      logger.warn("Staff save failed:", res.error.message);
      await alertDialog({ message: `Could not save:\n\n${res.error.message}` });
      return;
    }
    logAudit("staff_user_updated", {
      target_id: editing.id,
      role: form.role,
      login_method: form.login_method || "password",
      is_consultant: form.is_consultant === true,
      by: user.email,
    });
    setEditing(null);
    loadUsers();
    showSaved(`Staff member updated ${GLYPH.check}`);
  }

  async function saveEdit() {
    // editing is { id, table } — "shelters" | "profiles" | "nologin"
    if (!editing) return;
    setSaving(true);

    if (editing.table === "nologin") {
      await saveNoLoginContact();
      return;
    }

    if (editing.table === "shelters") {
      // Save org fields to shelters table
      updateShelterById(editing!.id, {
        shelter_name: form.shelter_name,
        shelter_code: form.shelter_code,
        state: form.state,
        customer_type: form.customer_type,
        sales_group: form.sales_group,
        taxable: form.taxable !== false,
        postal_line1: form.postal_line1,
        postal_line2: form.postal_line2,
        postal_suburb: form.postal_suburb,
        postal_suburb_city: form.postal_suburb_city,
        postal_state: form.postal_state,
        postal_postcode: form.postal_postcode,
        unleashed_guid: form.unleashed_guid,
        internal_notes: form.internal_notes,
        date_onboarded: form.date_onboarded || null,
        updated_by: user.email,
      }).then(function (res) {
        setSaving(false);
        if (res.error) {
          logger.warn("Save failed:", res.error.message);
          return;
        }
        logAudit("shelter_updated", {
          shelter_id: editing!.id,
          shelter_code: form.shelter_code || "",
          shelter_name: form.shelter_name || "",
          by: user.email,
        });
        setEditing(null);
        loadUsers();
        showSaved(`DFV Service updated ${GLYPH.check}`);
      });
    } else {
      // Contact-model No-Login: ticking "No login account" on a login user retires
      // their login (they become contact-only). Short-circuit before the normal
      // profile+membership save — removeLoginForUser guards + routes to the EF.
      if (form.no_login === true) {
        await removeLoginForUser();
        return;
      }
      // Save person/login fields to profiles table
      // Validate shelter_code exists if provided
      if (form.shelter_code && form.role === ROLES.SHELTER) {
        const scRow = await getShelterByCode(form.shelter_code);
        if (!scRow) {
          setSaving(false);
          await alertDialog({
            message:
              "Shelter code '" +
              form.shelter_code +
              "' does not exist. Please create the shelter first, or correct the code.",
          });
          return;
        }
      }
      const hasName = (form.first_name || "").trim() || (form.last_name || "").trim();
      const isGeneric = form.is_generic || (!hasName && form.role === ROLES.SHELTER);
      const displayName = hasName
        ? `${form.first_name} ${form.last_name}`.trim()
        : isGeneric && form.shelter_name
          ? `${form.shelter_name} — Shared`
          : form.display_name || "";
      // Build shelter_codes array: always include primary shelter_code + any extras
      const finalShelterCodes = Array.isArray(form.shelter_codes) ? form.shelter_codes.slice() : [];
      if (form.shelter_code && finalShelterCodes.indexOf(form.shelter_code) === -1) {
        finalShelterCodes.unshift(form.shelter_code);
      }
      // LOGIN EMAIL change — if the email differs from what was loaded, route
      // it through the admin-users Edge Function. The Edge Function updates
      // BOTH auth.users.email AND profiles.user_email with the service-role
      // client, so they stay in sync. If we did the profile update from here
      // without that, the user couldn't log in with the new email.
      // Extracted to changeLoginEmailIfNeeded() so the staff save path shares it —
      // being a login is the one thing both populations have in common.
      if (!(await changeLoginEmailIfNeeded())) return;
      // Stage 2: stop writing shelter-derived fields (state, shelter_name,
      // shelter_address) to profiles — they live on the shelters table now.
      // Stage 2 follow-up: shelter_codes is also no longer written to profiles
      // (user_shelters rows are written below — the canonical source).
      // Contact-model Phase 3 (Stage 1 de-wire): the account name lives on staff_*;
      // the legacy profiles.first_name/last_name/user_email/phones/primary_contact_label
      // are NO LONGER written here (they drop in Stage 2). The per-service CONTACT
      // (name/email/phone/mobile/role) lives on the user_shelters membership rows,
      // written below. The login email is handled by the admin-users EF above.
      // ⚠️ THE WALL (docs/staff-caseworker-separation-plan.md) — this is the
      // CASEWORKER save path (the Zephyr Staff editor has its own, saveStaffEdit).
      // A caseworker's profile carries LOGIN MECHANICS ONLY. It must NEVER be given
      // staff_first_name / staff_last_name (their name lives on the user_shelters
      // membership contact, written below) nor the Zephyr-Staff-only fields
      // staff_mobile / staff_colour / show_on_staff_list. All five used to be
      // written here — that is the defect this work exists to remove.
      //
      // display_name stays: it is the shared login label for BOTH populations
      // (renamed to display_name_allusers in a later phase).
      updateProfileById(editing!.id, {
        display_name: displayName,
        is_generic: isGeneric,
        // profiles.shelter_code is NOT written — user_shelters.is_primary is the
        // source of truth for the primary shelter (Phase 4 B4-B). The membership
        // rows are written below from finalShelterCodes with is_primary: idx===0.
        role: form.role,
        login_method: form.login_method || "password",
        updated_by: user.email,
      }).then(async function (res) {
        if (res.error) {
          setSaving(false);
          logger.warn("Save failed:", res.error.message);
          return;
        }
        // Sync user_shelters table: delete old rows, insert new. Identity Phase 2:
        // resolve each code to its shelter_id (the live key) and write BOTH columns
        // (shelter_code is still NOT NULL until Phase 3). Codes come from the form's
        // shelter picker, so they resolve; a stray unresolved code is skipped rather
        // than written as a null shelter_id (which the NOT NULL would reject).
        //
        // Contact-model 3c-iii (GOTCHA 1): the membership rows now carry the per-
        // service CONTACT, so a blind delete+reinsert would WIPE it. Capture the
        // existing per-service contact_label map AND the row ids BEFORE the delete:
        //  - person-level fields (name/email/phone/mobile) fan out onto EVERY row
        //    (the contact IS the person);
        //  - contact_label is per-service — restore each service's prior label and
        //    override only the edited service's (editing.shelterId) with the form value;
        //  - the delete also nulls shelters.primary_contact (FK ON DELETE SET NULL,
        //    db-90) for any service this user is Primary of, so re-point those after.
        const priorRows = (await listUserSheltersByUserId(editing!.id ?? "")) || [];
        const labelByShelterId: Record<string, string | null> = {};
        // Unleashed Contacts arm: capture the linked Contact GUID per service too, so the
        // reinsert below preserves it — otherwise a save orphans it and the sync would
        // create a duplicate contact in Unleashed.
        const guidByShelterId: Record<string, string | null> = {};
        priorRows.forEach(function (r) {
          if (r.shelter_id) {
            labelByShelterId[r.shelter_id] = r.contact_label ?? null;
            guidByShelterId[r.shelter_id] = r.unleashed_contact_guid ?? null;
          }
        });
        const priorMembershipIds = priorRows
          .map(function (r) {
            return r.id;
          })
          .filter(function (id): id is string {
            return !!id;
          });
        const primaryContactShelterIds =
          priorMembershipIds.length > 0
            ? await listShelterIdsByPrimaryContact(priorMembershipIds)
            : [];
        await deleteUserSheltersByUserId(editing!.id);
        if (finalShelterCodes.length > 0) {
          const shelterRows = (await listSheltersByCodes(finalShelterCodes)) || [];
          const idByCode: Record<string, string> = {};
          shelterRows.forEach(function (s) {
            if (s.shelter_code && s.id) idByCode[s.shelter_code] = s.id;
          });
          // The contact email IS the login email (account-level, one per person).
          const contactEmail = (form.user_email || "").trim() || null;
          const rows = finalShelterCodes
            .filter(function (code) {
              return idByCode[code];
            })
            .map(function (code, idx) {
              const sid = idByCode[code];
              const isEditedService = !!editing!.shelterId && sid === editing!.shelterId;
              return {
                user_id: editing!.id,
                shelter_id: sid,
                shelter_code: code,
                is_primary: idx === 0,
                // Person-level contact — fans out to all the user's services.
                first_name: form.first_name || null,
                last_name: form.last_name || null,
                email: contactEmail,
                // Normalise on save (belt-and-suspenders vs the onBlur formatter):
                // Phone → landline shape, Mobile → mobile shape.
                phone: formatPhone(form.phone, "landline") || null,
                mobile: formatPhone(form.mobile, "mobile") || null,
                // Per-service Role: edited service takes the form value; the rest
                // keep their existing label (null when the service is newly added).
                contact_label: isEditedService
                  ? form.contact_label || null
                  : (labelByShelterId[sid] ?? null),
                // Carry the existing service's linked Unleashed Contact GUID across the
                // delete+reinsert (null for a newly-added service, which has none yet).
                unleashed_contact_guid: guidByShelterId[sid] ?? null,
              };
            });
          if (rows.length > 0) {
            const insRes = await insertUserShelters(rows);
            if (insRes.error) {
              // Surface it — a silent failure here would drop the user's memberships
              // (and their contact fan-out) without anyone noticing.
              setSaving(false);
              await alertDialog({
                title: "Couldn't save memberships",
                message:
                  "The user's DFV Service memberships could not be saved:\n\n" +
                  `${insRes.error.message}\n\nPlease retry — their contact may be incomplete.`,
              });
              return;
            }
          }
        }
        // Re-point any Primary-contact pointers the delete nulled, to the fresh
        // membership row (only for services the user still belongs to; a service the
        // edit removed them from correctly stays without them as Primary).
        for (const sid of primaryContactShelterIds) {
          await setPrimaryShelterUser(sid, editing!.id ?? "");
        }
        // Unleashed Contacts arm: the reinsert changed the person's contact across their
        // services — enqueue an upsert per membership (GUIDs preserved by C5-pre, so the
        // worker UPDATEs rather than duplicates). Fire-and-forget. Stamp a display-only
        // field diff (name/email/phone/mobile: prior contact vs the edited form) so the
        // Push List inspect modal shows WHAT changed; the person-level edit is the same
        // across every membership, so one diff rides them all.
        const prior0 = priorRows[0] || {};
        const contactChanges = computeContactChanges(
          {
            first_name: prior0.first_name,
            last_name: prior0.last_name,
            email: prior0.email,
            phone: prior0.phone,
            mobile: prior0.mobile,
          },
          {
            first_name: form.first_name,
            last_name: form.last_name,
            email: (form.user_email || "").trim(),
            phone: formatPhone(form.phone, "landline"),
            mobile: formatPhone(form.mobile, "mobile"),
          }
        );
        void enqueueContactPushForUser(
          editing!.id ?? "",
          "upsert",
          contactChanges.length ? { changes: contactChanges } : undefined
        );
        setSaving(false);
        logAudit("user_updated", {
          target_id: editing!.id,
          role: form.role,
          shelter: form.shelter_code,
          login_method: form.login_method || "password",
          by: user.email,
        });
        setEditing(null);
        loadUsers();
        showSaved(`User updated ${GLYPH.check}`);
      });
    }
  }

  // A staff-initiated password reset. Goes through the admin-users Edge Function
  // (service-role admin API), NOT the client `resetPasswordForEmail`: since the 30 Aug
  // CAPTCHA rollout (Supabase Attack Protection) the client recovery endpoint requires a
  // Turnstile token, which this admin form has no widget for — so it 400'd with
  // "no captcha_token found". The EF mints a RECOVERY OTP (service role bypasses captcha)
  // and emails the CODE (docs/auth-email-code-entry-plan.md); the user enters it via
  // "Have an access code?". Mirrors resetUserMfa's EF-fetch shape. The public self-service
  // reset on the login page is unchanged (it has the widget).
  async function sendPasswordReset(email?: string | null) {
    if (!email) {
      await alertDialog({ message: "No email address on file for this user." });
      return;
    }
    if (!(await confirmDialog({ message: `Send password reset email to ${email}?` }))) return;
    // Immediate visual feedback — the toast appears within ms of confirming.
    showSaved(`Sending password reset to ${email}...`);
    const sessionRes = await sb().auth.getSession();
    const token = sessionRes.data?.session ? sessionRes.data.session.access_token : "";
    let res: { error?: string; success?: boolean } | undefined;
    try {
      const resp = await fetch(EDGE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: "send_password_reset", email }),
      });
      res = await resp.json();
    } catch (e) {
      await alertDialog({
        message:
          "Could not send password reset email — network error. Please try again.\n\n" +
          ((e as Error)?.message ? (e as Error).message : String(e)),
      });
      return;
    }
    if (res?.error) {
      await alertDialog({
        message:
          "Could not send password reset email:\n\n" +
          res.error +
          "\n\nIf the problem persists, contact the Zephyr admin team.",
      });
      logger.warn("Password reset error:", res.error);
      return;
    }
    logAudit("password_reset_sent", { target_email: email, by: user.email });
    showSaved(`Password reset email sent to ${email}`);
  }

  // Reset a staff member's MFA (lost authenticator). Privileged — goes through the
  // admin-users Edge Function (service-role auth.admin), not the client. The user
  // is forced to re-enrol on their next login (src/utils/mfa.ts); no email is sent
  // (this is done with the user on the phone). Audited as `mfa_reset`.
  async function resetUserMfa(userId?: string | null, email?: string | null) {
    if (!userId) {
      await alertDialog({ message: "No user selected." });
      return;
    }
    const who = email || "this user";
    if (
      !(await confirmDialog({
        title: "Reset MFA?",
        message: `Reset multi-factor authentication for ${who}? Their current authenticator app will stop working and they'll be required to set up MFA again the next time they log in.`,
        confirmLabel: "Reset MFA",
        danger: true,
      }))
    )
      return;
    showSaved(`Resetting MFA for ${who}...`);
    const sessionRes = await sb().auth.getSession();
    const token = sessionRes.data?.session ? sessionRes.data.session.access_token : "";
    let res: { error?: string; success?: boolean } | undefined;
    try {
      const resp = await fetch(EDGE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: "reset_mfa", user_id: userId }),
      });
      res = await resp.json();
    } catch (e) {
      await alertDialog({
        message:
          "Failed to reset MFA — network error. Please try again.\n\n" +
          ((e as Error)?.message ? (e as Error).message : String(e)),
      });
      return;
    }
    if (res?.error) {
      await alertDialog({ message: `Failed to reset MFA:\n\n${res.error}` });
      return;
    }
    logAudit("mfa_reset", { target_id: userId, target_email: email, by: user.email });
    showSaved(`MFA reset for ${who} ${GLYPH.check}`);
  }

  // `kindArg` ("shelters" | "caseworkers" | "staff") says what to create.
  // Defaults to the list's activeSection for the tab-bar path; the shelter
  // detail's Users tab passes "caseworkers" explicitly (Phase 3 — creation is
  // decoupled from the tabs so the Caseworkers tab can be retired).
  // Create a login account (auth + profile + user_shelters membership) via the
  // admin-users edge function. EXTRACTED from createUser (unchanged logic) so both
  // the caseworker/staff forms AND the New DFV Service wizard's seamless "create
  // service + first user" flow use the SAME battle-tested path. The callbacks own
  // the UI: onError surfaces the message, onDone runs the caller's reset/close.
  // `f` = the account form; `kind` = "staff" | "caseworkers" (role default). This
  // function has NO UI side-effects of its own beyond the callbacks + the audit log.
  async function createCaseworkerAccount(
    f: NewUserForm,
    kind: string,
    cb: { onError: (msg: string) => void; onDone: (userId: string | null) => void }
  ) {
    const role =
      kind === "staff" ? (f.role !== ROLES.SHELTER ? f.role : ROLES.STAFF) : ROLES.SHELTER;
    const hasName = (f.first_name || "").trim() || (f.last_name || "").trim();
    const isGeneric = !hasName && role === ROLES.SHELTER;
    const displayName = hasName
      ? `${f.first_name} ${f.last_name}`.trim()
      : isGeneric
        ? f.shelter_code
          ? `${f.shelter_code} — Shared`
          : "Shared"
        : "";
    const isZephyrEmail = /@zephyreducation\.com\.au$/i.test((f.email || "").trim());
    const loginMethod = f.login_method || (isZephyrEmail ? "google" : "password");
    const sendWelcome = f.send_welcome === undefined ? !isZephyrEmail : f.send_welcome;
    // Look up shelter name for the onboarding email (shelter accounts only).
    const resolvedShelterName =
      role === ROLES.SHELTER && f.shelter_code
        ? await getShelterByCode(f.shelter_code).then(function (r) {
            return r?.shelter_name ? r.shelter_name : f.shelter_code;
          })
        : "";
    let res: { error?: string; user_id?: string; user?: { id?: string } };
    try {
      const sessionRes = await sb().auth.getSession();
      const token = sessionRes.data?.session ? sessionRes.data.session.access_token : "";
      const resp = await fetch(EDGE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          action: "create",
          email: f.email,
          password: f.password,
          display_name: displayName,
          shelter_name: resolvedShelterName || "",
          shelter_code: f.shelter_code,
          role: role,
          state: f.state,
          first_name: f.first_name,
          last_name: f.last_name,
          is_generic: isGeneric,
          login_method: loginMethod,
          send_welcome: sendWelcome,
        }),
      });
      res = await resp.json();
    } catch (e) {
      cb.onError(
        `Edge function error: ${(e as Error)?.message ? (e as Error).message : String(e)}`
      );
      return;
    }
    if (res.error) {
      cb.onError(res.error);
      return;
    }
    const newUserId = res.user_id || res.user?.id || null;
    // Safety net: upsert the profile from the client — the edge function is supposed
    // to create it, but we've seen the auth row land without a matching profile.
    if (newUserId) {
      try {
        // ⚠️ THE WALL: staff_* is written ONLY for a Zephyr staff account. A
        // caseworker's name/email live on their user_shelters membership contact
        // (written below); their profile carries login mechanics only.
        //
        // ⚠️ createUser is still SHARED by the Zephyr Staff and caseworker "+ New
        // User" flows, so this is a role gate rather than a clean split — unlike
        // editUser, which was split into saveStaffEdit + saveEdit. Splitting
        // createUser the same way is outstanding work (see the plan / tracker); the
        // gate keeps the DATA correct in the meantime.
        const isStaffAccount = role !== ROLES.SHELTER;
        const profileRow = {
          id: newUserId,
          display_name: displayName,
          ...(isStaffAccount
            ? {
                staff_user_email: f.email,
                staff_first_name: f.first_name || null,
                staff_last_name: f.last_name || null,
                // "Display Name" (db-184) — the short public name shown to shelters. Use the form
                // value if given, else default to "<first> <last-initial> (Zephyr)" so a new staff
                // member is seeded just like the migration seeded existing ones.
                preferred_name:
                  (f.preferred_name || "").trim() ||
                  ((f.first_name || "").trim()
                    ? `${(f.first_name || "").trim()}${
                        (f.last_name || "").trim() ? ` ${(f.last_name || "").trim().charAt(0)}` : ""
                      } (Zephyr)`
                    : null),
                // Parity with the Zephyr Staff edit form (saveStaffEdit): default
                // shown on the Staff List; consultant off unless ticked.
                show_on_staff_list: f.show_on_staff_list !== false,
                is_consultant: f.is_consultant === true,
              }
            : {}),
          role: role,
          // No profiles.shelter_code — the user_shelters membership (written below
          // with is_primary: true) is the source of truth (Phase 4 B4-B).
          is_generic: isGeneric,
          login_method: loginMethod,
          // Invite tracking (db-44): a welcome email at creation IS the invitation.
          invited_at: sendWelcome ? new Date().toISOString() : null,
        };
        const profUp = await upsertProfile(profileRow);
        if (profUp.error) {
          cb.onError(`Profile create failed: ${profUp.error.message}`);
          return;
        }
      } catch (e) {
        cb.onError(
          `Profile create error: ${(e as Error)?.message ? (e as Error).message : String(e)}`
        );
        return;
      }
    }
    // The user_shelters membership row is what RLS checks at submit time — the
    // profile's shelter_code alone is NOT enough. Await + surface any failure so a
    // user is never left with a shelter_code but no membership (the silent-drift bug).
    if (f.shelter_code && newUserId) {
      // Identity Phase 2: the membership row is keyed on shelter_id. Prefer an id the
      // caller already has (the shelters-first-user path just created the service and
      // passes insRes.id), else resolve from the immutable code. shelter_code is still
      // written (NOT NULL until Phase 3).
      const shelterId = f.shelter_id || (await getShelterByCode(f.shelter_code))?.id;
      if (!shelterId) {
        cb.onError(`DFV Service membership create failed: shelter ${f.shelter_code} not found.`);
        return;
      }
      // The first user of a service with no Primary yet becomes its PRIMARY USER
      // (db-41) — covers the wizard first-user AND a later "+ New User" — so a service
      // is never left without a default. Never overrides an existing Primary.
      const needsPrimaryUser = !(await shelterHasPrimaryUser(shelterId));
      const usRes = await upsertUserShelter({
        user_id: newUserId,
        shelter_id: shelterId,
        shelter_code: f.shelter_code,
        is_primary: true,
        // Contact-model Phase 3 (Option A): seed the per-service CONTACT from the
        // create form (defaults from the account; per-service editable later). Login
        // email is the account key — the contact email defaults from it here.
        first_name: f.first_name || null,
        last_name: f.last_name || null,
        email: f.email || null,
        // Normalise on save (belt-and-suspenders vs the create form's onBlur formatter).
        phone: formatPhone(f.phone, "landline") || null,
        mobile: formatPhone((f as { mobile?: string | null }).mobile, "mobile") || null,
        contact_label: f.primary_contact_label || null,
      });
      if (usRes.error) {
        cb.onError(`DFV Service membership create failed: ${usRes.error.message}`);
        return;
      }
      // Contact-model Phase 2 (pointer-only): the first user of a service with no
      // Primary contact yet becomes it — point shelters.primary_contact at the new
      // membership. Never overrides an existing Primary.
      if (needsPrimaryUser) await setPrimaryShelterUser(shelterId, newUserId);
      // Unleashed Contacts arm: enqueue an upsert for the new contact (Zephyr-gated,
      // re-lists to get the fresh membership id). Fire-and-forget.
      void enqueueContactPushForUser(newUserId, "upsert");
    }
    logAudit("user_created", {
      new_user_id: newUserId,
      email: f.email,
      role: role,
      shelter_code: f.shelter_code || "",
      by: user.email,
    });
    cb.onDone(newUserId);
  }

  async function createUser(kindArg?: string) {
    const kind = kindArg || activeSection;
    setNewErr("");

    // Shelter kind: insert org record into shelters table — no auth account needed
    if (kind === "shelters") {
      if (!newForm.shelter_name || !newForm.shelter_code) {
        setNewErr("DFV Service Name and DFV Service Code are required.");
        return;
      }
      // A default delivery address (street entered) needs a name — it prints on the
      // delivery; no more auto "Default Delivery" (David, 25 Oct 2026).
      if (newForm.delivery_line1 && isBlankAddressName(newForm.delivery_address_name)) {
        setNewErr(ADDRESS_NAME_REQUIRED_MSG);
        return;
      }
      // Contact-only first user (No Login): no auth account, so no password rules.
      // Require at least a name so the contact is identifiable (email optional).
      const cwNoLogin = newForm.cw_no_login === true;
      const cwHasName = !!(
        (newForm.cw_first_name || "").trim() || (newForm.cw_last_name || "").trim()
      );
      if (cwNoLogin && !cwHasName) {
        setNewErr(
          "Enter a name for the first contact, or untick 'No login account (contact only)'."
        );
        return;
      }
      // Validate the optional first-user (LOGIN) BEFORE creating the service, so a
      // bad password can never leave an orphaned service (the seamless create runs
      // the user only after the service insert succeeds). Skipped for a No-Login
      // contact, which has no password.
      if (!cwNoLogin && (newForm.cw_email || newForm.cw_password)) {
        if (!newForm.cw_email || !newForm.cw_password) {
          setNewErr(
            "Enter both an email and password for the first user, or clear both to add users later."
          );
          return;
        }
        if (newForm.cw_password.length < 12) {
          setNewErr("First user password must be at least 12 characters.");
          return;
        }
        if (!/[A-Z]/.test(newForm.cw_password)) {
          setNewErr("First user password must contain an uppercase letter.");
          return;
        }
        if (!/[0-9]/.test(newForm.cw_password)) {
          setNewErr("First user password must contain a number.");
          return;
        }
        if (!/[a-z]/.test(newForm.cw_password)) {
          setNewErr("First user password must contain a lowercase letter.");
          return;
        }
        if (!/[^A-Za-z0-9]/.test(newForm.cw_password)) {
          setNewErr("First user password must contain a symbol.");
          return;
        }
      }
      setNewSaving(true);
      getShelterByCode(newForm.shelter_code).then(function (res) {
        if (res) {
          setNewSaving(false);
          setNewErr(
            `DFV Service code '${newForm.shelter_code}' already exists. Choose a different code.`
          );
          return;
        }
        insertShelter({
          shelter_code: newForm.shelter_code,
          shelter_name: newForm.shelter_name,
          state: newForm.state,
          customer_type: newForm.customer_type,
          sales_group: newForm.sales_group,
          taxable: newForm.taxable !== false,
          stock_held: newForm.stock_held === true,
          // Contact-model Phase 3 (Stage 1 de-wire): shelters.email/phone/phones are
          // no longer written — the shelter's Email/Phone/Mobile derive from the
          // Primary contact's membership (ShelterDetail Details tab, read-only). The
          // legacy columns drop in Stage 2.
          abn: newForm.abn || null,
          default_warehouse: newForm.default_warehouse || null,
          reminder: newForm.reminder || null,
          // Postal address (all fields the Address tab carries).
          postal_line1: newForm.postal_line1 || null,
          postal_line2: newForm.postal_line2 || null,
          postal_suburb: newForm.postal_suburb || null,
          postal_suburb_city: newForm.postal_suburb_city || null,
          postal_state: newForm.postal_state || null,
          postal_postcode: newForm.postal_postcode || null,
          // Physical address (all fields the Address tab carries).
          physical_line1: newForm.physical_line1 || null,
          physical_line2: newForm.physical_line2 || null,
          physical_suburb: newForm.physical_suburb || null,
          physical_suburb_city: newForm.physical_suburb_city || null,
          physical_state: newForm.physical_state || null,
          physical_postcode: newForm.physical_postcode || null,
          internal_notes: newForm.internal_notes || null,
          date_onboarded: newForm.date_onboarded || new Date().toISOString().slice(0, 10),
        }).then(async function (insRes) {
          if (insRes.error) {
            setNewSaving(false);
            setNewErr(insRes.error.message);
            return;
          }
          // Auto-sync (db-133): a new DFV Service → queue a Customer create push
          // (self-gated to Zephyr* codes inside the helper).
          if (insRes.id) {
            void enqueueUnleashedPush({
              entityType: "customer",
              entityId: insRes.id,
              shelterCode: newForm.shelter_code,
            });
          }
          // Insert default delivery address if provided
          if (newForm.delivery_line1) {
            insertAddresses([
              {
                shelter_id: insRes.id,
                address_name: String(newForm.delivery_address_name).trim(),
                street: newForm.delivery_line1,
                street2: newForm.delivery_line2 || null,
                suburb_city: newForm.delivery_suburb || null,
                state: newForm.delivery_state || newForm.state || null,
                postcode: newForm.delivery_postcode || null,
                delivery_instruction: newForm.delivery_instructions || null,
                is_default: true,
                // Known-good at setup → show on the pick-list immediately (db-146).
                use_count: ADDRESS_PROMOTION_THRESHOLD,
              },
            ]).then(function (addrRes) {
              if (addrRes.error)
                logger.warn("Delivery address insert failed:", addrRes.error.message);
            });
          }
          // Contact-only first user (No Login): create a login-less user_shelters
          // membership and make it the new service's Primary contact. Mirrors the
          // caseworkers no_login path. The service is already inserted, so a wall
          // rejection (a @zephyreducation.com.au / internal email) is SURFACED with
          // the service kept — never undone (the admin adds the contact later).
          // THE WALL: insertContactMembership throws on an internal contact.
          if (cwNoLogin) {
            let cIns: { id: string | null; error: { message: string } | null };
            const wizardContactKept = async function (msg: string) {
              setNewSaving(false);
              setShowNew(false);
              setNewForm({ ...BLANK_NEW_USER_FORM });
              loadUsers();
              await alertDialog({
                message: `The DFV Service was created, but the first contact could not be added:\n\n${msg}\n\nAdd the contact from the service's Users tab.`,
              });
            };
            try {
              cIns = await insertContactMembership({
                user_id: null,
                shelter_id: insRes.id,
                shelter_code: newForm.shelter_code,
                is_primary: false,
                first_name: newForm.cw_first_name || null,
                last_name: newForm.cw_last_name || null,
                email: (newForm.cw_email || "").trim() || null,
                phone: formatPhone(newForm.cw_phone, "landline") || null,
                mobile: formatPhone(newForm.cw_mobile, "mobile") || null,
                contact_label: newForm.cw_primary_contact_label || "DFV Service Manager",
              });
            } catch (e) {
              await wizardContactKept((e as Error)?.message || String(e));
              return;
            }
            if (cIns.error) {
              await wizardContactKept(cIns.error.message);
              return;
            }
            // Brand-new service → this login-less contact becomes its Primary
            // contact. shelters.primary_contact points straight at the membership
            // (works for a login-less row, which setPrimaryShelterUser can't resolve).
            if (cIns.id && insRes.id) {
              await setPrimaryContactMembership(insRes.id, cIns.id);
              void enqueueContactPushForMembership(cIns.id, "upsert");
            }
            logAudit("no_login_contact_created", {
              membership_id: cIns.id,
              shelter_code: newForm.shelter_code,
              via: "new_service_wizard",
              by: user.email,
            });
            setNewSaving(false);
            setShowNew(false);
            setNewForm({ ...BLANK_NEW_USER_FORM });
            loadUsers();
            showSaved(`DFV Service + first contact created ${GLYPH.check}`);
            return;
          }
          // Seamless first-user: the service is created; if a first caseworker was
          // entered, create the account via the SAME proven path createUser uses (the
          // shared createCaseworkerAccount helper — no duplicated account logic). A
          // user failure does NOT undo the service; it's surfaced and the admin can add
          // the user later from the service's Users tab.
          if (newForm.cw_email && newForm.cw_password) {
            const svcCode = newForm.shelter_code;
            await createCaseworkerAccount(
              {
                email: newForm.cw_email,
                password: newForm.cw_password,
                first_name: newForm.cw_first_name || "",
                last_name: newForm.cw_last_name || "",
                phone: newForm.cw_phone || "",
                mobile: newForm.cw_mobile || "",
                primary_contact_label: newForm.cw_primary_contact_label || "DFV Service Manager",
                shelter_code: svcCode,
                // Just-created service: hand the membership write its id (no re-lookup).
                shelter_id: insRes.id || undefined,
                state: newForm.state,
                role: ROLES.SHELTER,
              },
              "caseworkers",
              {
                onError: async function (m: string) {
                  setNewSaving(false);
                  setShowNew(false);
                  setNewForm({ ...BLANK_NEW_USER_FORM });
                  loadUsers();
                  await alertDialog({
                    message: `The DFV Service was created, but the first user could not be created:\n\n${m}\n\nAdd the user from the service's Users tab.`,
                  });
                },
                onDone: function () {
                  setNewSaving(false);
                  setShowNew(false);
                  setNewForm({ ...BLANK_NEW_USER_FORM });
                  loadUsers();
                  showSaved(`DFV Service + first user created ${GLYPH.check}`);
                },
              }
            );
            return;
          }
          setNewSaving(false);
          setShowNew(false);
          setNewForm({
            email: "",
            password: "",
            first_name: "",
            last_name: "",
            shelter_name: "",
            shelter_code: "",
            role: "shelter",
            state: "",
            customer_type: "",
            sales_group: "",
            taxable: false,
            phone: "",
            primary_contact_label: "Shelter Manager",
            postal_line1: "",
            postal_suburb: "",
            postal_postcode: "",
            internal_notes: "",
            delivery_line1: "",
            delivery_line2: "",
            delivery_suburb: "",
            delivery_state: "",
            delivery_postcode: "",
            delivery_instructions: "",
            delivery_address_name: "",
          });
          loadUsers();
          showSaved(`DFV Service created ${GLYPH.check}`);
        });
      });
      return;
    }

    // Contact-only (No Login) caseworker: create a login-less user_shelters
    // membership — NO auth account, NO password. Mirrors the Contactsimport
    // contact-only path (user_id NULL). The person can be given a login later
    // by un-ticking "No login" on their edit form. THE WALL: insertContactMembership
    // rejects a @zephyreducation.com.au / internal contact.
    if (kind === "caseworkers" && newForm.no_login === true) {
      if (!newForm.shelter_code) {
        setNewErr("DFV Service Code is required for a contact.");
        return;
      }
      setNewSaving(true);
      const scRow0 = await getShelterByCode(newForm.shelter_code);
      if (!scRow0) {
        setNewSaving(false);
        setNewErr(
          "DFV Service code '" +
            newForm.shelter_code +
            "' does not exist. Create the DFV Service first via the DFV Services tab."
        );
        return;
      }
      let ins: { id: string | null; error: { message: string } | null };
      try {
        ins = await insertContactMembership({
          user_id: null,
          shelter_id: scRow0.id,
          shelter_code: newForm.shelter_code,
          is_primary: false,
          first_name: newForm.first_name || null,
          last_name: newForm.last_name || null,
          email: (newForm.email || "").trim() || null,
          phone: formatPhone(newForm.phone, "landline") || null,
          mobile: formatPhone(newForm.mobile, "mobile") || null,
          contact_label: newForm.primary_contact_label || null,
        });
      } catch (e) {
        // THE WALL: a @zephyreducation.com.au / internal email is rejected before
        // the insert (assertNotZephyrContact throws). Surface it, don't crash.
        setNewSaving(false);
        setNewErr((e as Error)?.message || String(e));
        return;
      }
      setNewSaving(false);
      if (ins.error) {
        setNewErr(contactDupMessage(ins.error.message));
        return;
      }
      logAudit("no_login_contact_created", {
        membership_id: ins.id,
        shelter_code: newForm.shelter_code,
        by: user.email,
      });
      if (ins.id) void enqueueContactPushForMembership(ins.id, "upsert");
      setShowNew(false);
      setNewForm({ ...BLANK_NEW_USER_FORM });
      loadUsers();
      showSaved(`Contact created ${GLYPH.check}`);
      return;
    }

    // Caseworker / Staff tabs: create auth account via edge function
    if (!newForm.email || !newForm.password) {
      setNewErr("Email and password are required.");
      return;
    }
    if (newForm.password.length < 12) {
      setNewErr("Password must be at least 12 characters.");
      return;
    }
    if (!/[A-Z]/.test(newForm.password)) {
      setNewErr("Password must contain at least one uppercase letter.");
      return;
    }
    if (!/[0-9]/.test(newForm.password)) {
      setNewErr("Password must contain at least one number.");
      return;
    }
    // Supabase enforces FIVE rules (length 12, lower, upper, digit, symbol) — this path
    // only checked three, so a password like PASSWORD12345 passed the form and came back
    // as a raw Edge Function error. The first-user path above already checks all five,
    // and contactsImport's generator prefixes "Zi9!" for exactly these gates.
    // Finding B16 item 7, 18 Aug 2026.
    if (!/[a-z]/.test(newForm.password)) {
      setNewErr("Password must contain at least one lowercase letter.");
      return;
    }
    if (!/[^A-Za-z0-9]/.test(newForm.password)) {
      setNewErr("Password must contain at least one symbol.");
      return;
    }
    if (kind === "caseworkers" && !newForm.shelter_code) {
      setNewErr("DFV Service Code is required for caseworker accounts.");
      return;
    }
    setNewSaving(true);
    // Validate shelter code exists before creating account
    if (kind === "caseworkers") {
      const scCheck = await getShelterByCode(newForm.shelter_code);
      if (!scCheck) {
        setNewSaving(false);
        setNewErr(
          "DFV Service code '" +
            newForm.shelter_code +
            "' does not exist. Create the DFV Service first via the DFV Services tab."
        );
        return;
      }
    }
    await createCaseworkerAccount(newForm, kind, {
      onError: function (m: string) {
        setNewSaving(false);
        setNewErr(m);
      },
      onDone: function () {
        setNewSaving(false);
        setShowNew(false);
        setNewForm({
          email: "",
          password: "",
          first_name: "",
          last_name: "",
          shelter_name: "",
          shelter_code: "",
          role: "shelter",
          state: "",
          customer_type: "",
          sales_group: "",
          taxable: false,
          phone: "",
          primary_contact_label: "Shelter Manager",
          postal_line1: "",
          postal_suburb: "",
          postal_postcode: "",
          internal_notes: "",
          delivery_line1: "",
          delivery_line2: "",
          delivery_suburb: "",
          delivery_state: "",
          delivery_postcode: "",
          delivery_instructions: "",
          delivery_address_name: "",
        });
        loadUsers();
        // Tell the admin whether the account's welcome/invitation email went out
        // (a caseworker create always sends it; a @zephyreducation SSO staff account
        // defaults to no email). John, 8 Aug: "it just needs to tell me when it saves".
        const createdEmail = newForm.email || "";
        const isZephyrEmail = /@zephyreducation\.com\.au$/i.test(createdEmail.trim());
        const welcomeSent =
          newForm.send_welcome === undefined ? !isZephyrEmail : newForm.send_welcome;
        showSaved(
          welcomeSent
            ? `User created — invitation email sent to ${createdEmail} ${GLYPH.check}`
            : `User created (no welcome email) ${GLYPH.check}`
        );
      },
    });
  }

  // Ban/unban a login in Auth via the edge function. Returns true on success,
  // false on any failure (no messaging here — callers decide). Defense in depth:
  // the profile `deactivated` flag is the authoritative app-level block (Phase 3).
  async function banAuthUser(userId: string | undefined, action: "deactivate" | "reactivate") {
    try {
      const sr = await sb().auth.getSession();
      const tok = sr.data?.session ? sr.data.session.access_token : "";
      const banRes = await fetch(EDGE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ action: action, user_id: userId ?? "" }),
      });
      const banJson = await banRes.json().catch(function () {
        return {};
      });
      return !!banRes.ok && !banJson.error;
    } catch (_e) {
      return false;
    }
  }

  function deactivateUser(u: DeactivateTarget) {
    const isDeactivating = !u.deactivated;
    const action = isDeactivating ? "deactivate" : "reactivate";
    // Update the profile flag directly. This flag is the AUTHORITATIVE app-level
    // block — login + session-restore refuse a deactivated profile (Phase 3,
    // docs/account-shelter-lifecycle-plan.md), so the Auth ban below is defense in
    // depth rather than the sole gate.
    updateProfileById(u.id, { deactivated: isDeactivating }).then(async function (res) {
      if (res.error) {
        logger.warn("Profile update error:", res.error.message);
        return;
      }
      setConfirmDeactivate(null);
      loadUsers();
      logAudit(isDeactivating ? "user_deactivated" : "user_reactivated", {
        target_id: u.id,
        email: u.staff_user_email,
        by: user.email,
      });
      showSaved(
        isDeactivating ? `User deactivated ${GLYPH.check}` : `User reactivated ${GLYPH.check}`
      );
      // Unleashed Contacts arm: deactivate DELETEs each of the person's contacts (the
      // membership rows survive, so the worker reads the GUIDs); reactivate re-creates
      // them (new GUIDs). Zephyr-gated + fire-and-forget.
      void enqueueContactPushForUser(u.id, isDeactivating ? "delete" : "upsert");
      // A failed Auth ban is audited + surfaced rather than swallowed.
      const banned = await banAuthUser(u.id, action);
      if (!banned) {
        logAudit("auth_ban_failed", { target_id: u.id, action: action, by: user.email });
        await alertDialog({
          title: "Login ban not applied",
          message: isDeactivating
            ? "The user is deactivated and blocked from the portal, but the Auth-level login ban did not apply. You may want to retry."
            : "The user is reactivated, but the Auth-level unban did not apply — they may be unable to log in until it is retried.",
        });
      }
    });
  }

  // Deactivate one or more shelters (no worker cascade). Used by the
  // last-active-worker offer when deactivating a shelter's final worker (decision
  // 1, docs/account-shelter-lifecycle-plan.md) — there the worker is already being
  // deactivated, so only the orphaned shelter row needs flagging. Audits each.
  async function deactivateShelters(shelterRows: Array<{ id: string; shelter_code?: string }>) {
    for (const s of shelterRows) {
      const res = await updateShelterById(s.id, { deactivated: true, updated_by: user.email });
      if (res.error) {
        logger.warn("Shelter deactivate failed:", res.error.message);
        continue;
      }
      logAudit("shelter_deactivated", {
        shelter_id: s.id,
        shelter_code: s.shelter_code || "",
        by: user.email,
        via: "last_worker_offer",
      });
    }
    loadUsers();
  }

  // Phase 4 — the standalone shelter-deactivate flow (decision 2, CASCADE): retire
  // the shelter, then for its active workers:
  //   - `workers` (EXCLUSIVE to this shelter) are fully deactivated — profile flag
  //     + Auth ban + audit, same as deactivateUser ("shelter_cascade").
  //   - `sharedWorkers` (also in another shelter) are DETACHED from this shelter
  //     only — their membership row is removed, account + other shelters untouched.
  async function deactivateShelterCascade(
    shelter: { id: string; shelter_code?: string; shelter_name?: string },
    workers: Array<{ id: string; user_email?: string }>,
    sharedWorkers: Array<{
      id: string;
      user_email?: string;
      profileShelterCode?: string | null;
    }> = []
  ) {
    const code = shelter.shelter_code || "";
    const sRes = await updateShelterById(shelter.id, {
      deactivated: true,
      updated_by: user.email,
    });
    if (sRes.error) {
      logger.warn("Shelter deactivate failed:", sRes.error.message);
      await alertDialog({
        title: "Couldn't deactivate",
        message: "The DFV Service could not be deactivated. Please try again.",
      });
      return;
    }
    logAudit("shelter_deactivated", {
      shelter_id: shelter.id,
      shelter_code: code,
      by: user.email,
      via: "shelter_cascade",
    });
    let banFailures = 0;
    for (const w of workers) {
      const pRes = await updateProfileById(w.id, { deactivated: true });
      if (pRes.error) {
        logger.warn("Cascade worker deactivate failed:", pRes.error.message);
        continue;
      }
      logAudit("user_deactivated", {
        target_id: w.id,
        email: w.user_email,
        by: user.email,
        via: "shelter_cascade",
      });
      const banned = await banAuthUser(w.id, "deactivate");
      if (!banned) {
        banFailures += 1;
        logAudit("auth_ban_failed", { target_id: w.id, action: "deactivate", by: user.email });
      }
    }
    // Shared workers: detach from THIS shelter only (keep account + other shelters).
    for (const w of sharedWorkers) {
      // Identity Phase 2: detach + repoint key on shelter_id. We have the shelter's
      // id here (shelter.id); the repoint target's id comes from the membership rows.
      const dRes = await deleteUserShelter(w.id, shelter.id);
      if (dRes.error) {
        logger.warn("Cascade worker detach failed:", dRes.error.message);
        continue;
      }
      logAudit("shelter_membership_removed", {
        target_id: w.id,
        email: w.user_email,
        shelter_code: code,
        by: user.email,
        via: "shelter_cascade",
      });
      // If this shelter was the worker's PRIMARY, repoint to one of their remaining
      // shelters by setting is_primary in user_shelters (the source of truth). Avoids
      // the primary pointing at a shelter they're no longer a member of.
      if (w.profileShelterCode && w.profileShelterCode === code) {
        const remaining = (await listUserSheltersByUserId(w.id)) || [];
        const newPrimaryId = remaining.length > 0 ? remaining[0].shelter_id : null;
        const newPrimaryCode = remaining.length > 0 ? remaining[0].shelter_code : "";
        if (newPrimaryId) await setPrimaryShelter(w.id, newPrimaryId);
        logAudit("primary_shelter_repointed", {
          target_id: w.id,
          from: code,
          to: newPrimaryCode,
          by: user.email,
          via: "shelter_cascade",
        });
      }
    }
    loadUsers();
    if (banFailures > 0) {
      await alertDialog({
        title: "Some login bans not applied",
        message: `The DFV Service and its workers are deactivated and blocked from the portal, but ${banFailures} Auth-level login ban(s) did not apply.`,
      });
    }
    showSaved(`DFV Service deactivated ${GLYPH.check}`);
  }

  // Reactivate a shelter. Does NOT auto-reactivate its workers — they were
  // deactivated independently (Phase 5 covers the "no active worker" warning).
  async function reactivateShelter(shelter: { id: string; shelter_code?: string }) {
    const res = await updateShelterById(shelter.id, {
      deactivated: false,
      updated_by: user.email,
    });
    if (res.error) {
      logger.warn("Shelter reactivate failed:", res.error.message);
      await alertDialog({
        title: "Couldn't reactivate",
        message: "The DFV Service could not be reactivated. Please try again.",
      });
      return;
    }
    logAudit("shelter_reactivated", {
      shelter_id: shelter.id,
      shelter_code: shelter.shelter_code || "",
      by: user.email,
    });
    loadUsers();
    showSaved(`DFV Service reactivated ${GLYPH.check}`);
  }

  // ── Users-tab membership actions (Phase 3, shelter-admin redesign) ─────────
  // Attach an EXISTING user to a shelter (the multi-shelter caseworker path —
  // without this, retiring the global Caseworkers roster would make a
  // second-shelter attachment impossible). First-ever membership also becomes
  // the user's primary via user_shelters.is_primary (the source of truth).
  async function attachUserToShelter(userId: string, shelterCode: string, userEmail?: string) {
    const existing = (await listUserSheltersByUserId(userId)) || [];
    if (
      existing.some(function (m) {
        return m.shelter_code === shelterCode;
      })
    ) {
      await alertDialog({ message: "That user is already attached to this DFV Service." });
      return;
    }
    // Identity Phase 2: key the membership on shelter_id (resolved from the
    // immutable code); shelter_code is still written (NOT NULL until Phase 3).
    const shelterRow = await getShelterByCode(shelterCode);
    if (!shelterRow?.id) {
      await alertDialog({
        message: `Could not attach the user: DFV Service ${shelterCode} not found.`,
      });
      return;
    }
    const isFirst = existing.length === 0;
    // If the service has no Primary contact yet, the attached user becomes it, so a
    // service is never left without a default. Never overrides an existing one.
    const needsPrimaryUser = !(await shelterHasPrimaryUser(shelterRow.id));
    // The membership row IS the Contact (Unleashed Customer Contact), so it must
    // carry the person's name/email/phone — not just the link. Copy them from one of
    // this person's EXISTING memberships: the contact is the person, so their details
    // already live on their other service rows.
    //
    // ⚠️ Deliberately NOT read from `profiles.staff_*` — those are for ZEPHYR STAFF
    // accounts only (John, 6 Aug 2026). A DFV Service caseworker's name/email/phone
    // lives ONLY on user_shelters.
    //
    // Without this the new row had blank first_name/last_name/email, and the contact
    // push then sent Unleashed an empty contact → HTTP 400 "At least one of: First
    // Name, Last Name, Email fields needs to be populated" (6 Aug 2026).
    const contactSource =
      existing.find(function (m) {
        return m.is_primary && (m.first_name || m.last_name || m.email);
      }) ||
      existing.find(function (m) {
        return m.first_name || m.last_name || m.email;
      }) ||
      null;
    const res = await upsertUserShelter({
      user_id: userId,
      shelter_id: shelterRow.id,
      shelter_code: shelterCode,
      is_primary: isFirst,
      first_name: contactSource?.first_name ?? null,
      last_name: contactSource?.last_name ?? null,
      email: contactSource?.email ?? userEmail ?? null,
      phone: contactSource?.phone ?? null,
      mobile: contactSource?.mobile ?? null,
    });
    if (res.error) {
      await alertDialog({ message: `Could not attach the user:\n\n${res.error.message}` });
      return;
    }
    // Contact-model Phase 2 (pointer-only): point shelters.primary_contact at this
    // membership when the service had no Primary contact.
    if (needsPrimaryUser) await setPrimaryShelterUser(shelterRow.id, userId);
    // Unleashed Contacts arm: enqueue an upsert for the newly-attached service's contact.
    void enqueueContactPushForUser(userId, "upsert");
    // First membership is primary via upsertUserShelter({ is_primary: isFirst })
    // above — no profiles.shelter_code mirror write (Phase 4 B4-B).
    logAudit("shelter_membership_added", {
      target_id: userId,
      email: userEmail || "",
      shelter_code: shelterCode,
      by: user.email,
      via: "users_tab",
    });
    loadUsers();
    showSaved(`User attached ${GLYPH.check}`);
  }

  // Detach a user from ONE shelter (account + other shelters kept) — the manual
  // twin of the shelter-deactivation cascade's shared-worker detach, with the
  // same primary-repoint semantics. Blocked when it's the user's ONLY shelter
  // (that would strand a shelter-role account with no membership — the
  // session-build refusal path): deactivate the user instead.
  async function detachUserFromShelter(
    u: { id: string; staff_user_email?: string | null; display_name?: string | null },
    shelterCode: string
  ) {
    const memberships = (await listUserSheltersByUserId(u.id)) || [];
    const thisRow = memberships.find(function (m) {
      return m.shelter_code === shelterCode;
    });
    const remaining = memberships.filter(function (m) {
      return m.shelter_code !== shelterCode;
    });
    if (remaining.length === 0) {
      await alertDialog({
        title: "Can't remove",
        message:
          "This is the user's only shelter — removing it would leave their account unable to log in. Deactivate the user instead.",
      });
      return;
    }
    const who = u.display_name || u.staff_user_email || "this user";
    if (
      !(await confirmDialog({
        title: "Remove from this DFV Service?",
        message: `Remove ${who} from ${shelterCode}? They keep their account and their other DFV Service(s).`,
        confirmLabel: "Remove",
        danger: true,
      }))
    )
      return;
    // Unleashed Contacts arm: capture the GUIDs + enqueue the contact DELETE BEFORE the
    // membership row is removed (the worker can't read them back afterwards). Awaited so
    // the capture wins the race; Zephyr-gated + no-op when never linked.
    await enqueueContactDeleteForMembership(thisRow?.id ?? "");
    // Identity Phase 2: delete + repoint key on shelter_id, taken from the
    // membership rows we already loaded (they now carry shelter_id).
    const dRes = await deleteUserShelter(u.id, thisRow?.shelter_id ?? "");
    if (dRes.error) {
      await alertDialog({ message: `Could not remove the user:\n\n${dRes.error.message}` });
      return;
    }
    logAudit("shelter_membership_removed", {
      target_id: u.id,
      email: u.staff_user_email || "",
      shelter_code: shelterCode,
      by: user.email,
      via: "users_tab",
    });
    // Repoint the user's primary if this shelter was it (same as the cascade).
    if (thisRow?.is_primary) {
      const newPrimaryId = remaining[0].shelter_id;
      const newPrimaryCode = remaining[0].shelter_code;
      if (newPrimaryId) await setPrimaryShelter(u.id, newPrimaryId);
      logAudit("primary_shelter_repointed", {
        target_id: u.id,
        from: shelterCode,
        to: newPrimaryCode,
        by: user.email,
        via: "users_tab",
      });
    }
    loadUsers();
    showSaved(`User removed from DFV Service ${GLYPH.check}`);
  }

  return {
    openDeliveryModal: openDeliveryModal,
    startNewAddr: startNewAddr,
    startEditAddr: startEditAddr,
    saveAddr: saveAddr,
    reactivateAddr: reactivateAddr,
    promoteAddr: promoteAddr,
    setAddrBanned: setAddrBanned,
    setAddrSilent: setAddrSilent,
    deleteAddr: deleteAddr,
    setDefault: setDefault,
    saveEdit: saveEdit,
    // Zephyr Staff save — SEPARATE from saveEdit by design (the wall). Only the
    // Zephyr Staff list uses it; never the caseworker Users tab.
    saveStaffEdit: saveStaffEdit,
    sendPasswordReset: sendPasswordReset,
    resetUserMfa: resetUserMfa,
    createUser: createUser,
    deactivateUser: deactivateUser,
    deactivateShelters: deactivateShelters,
    deactivateShelterCascade: deactivateShelterCascade,
    reactivateShelter: reactivateShelter,
    attachUserToShelter: attachUserToShelter,
    detachUserFromShelter: detachUserFromShelter,
    removeNoLoginContact: removeNoLoginContact,
    reactivateNoLoginContact: reactivateNoLoginContact,
  };
}
